<template>
  <CSidebar
    id="main-sidebar-navigation"
    overlaid
    container="lg"
    position="fixed"
    :key="componentKey"
    :class="{ hide: !show }"
    class="the-sidebar sidebar-lg-show v3-the-sidebar"
    :unfoldable="false"
    :visible="show"
    @visible-change="
      (value) => $store.commit('layout/set', ['sidebarShow', value])
    "
    role="navigation"
    aria-label="Main Navigation"
  >
    <div class="v3-sidebar-header" role="region" aria-label="Sidebar Header">
      <div
        class="v3-brand-container text-center"
        :to="userRole == 'student' ? '/passes/create/' : '/'"
      >
        <img
          width="100"
          id="logo-ehp"
          src="@/assets/images/pass-color-logo.png"
          alt="PASS Logo"
        />
      </div>
      <SidebarToggler
        :active="show"
        aria-controls="main-sidebar-navigation"
        data-test-id="mobile-sidebar-toggler"
        @click="$store.commit('layout/set', ['sidebarShow', !show])"
        aria-label="Toggle Sidebar"
      />
    </div>

    <!-- Reflow / ~400% on 1280px (≤991 CSS px): one native scroll for user block + nav. Desktop: nav-only PS. -->
    <template v-if="isSidebarReflowLayout">
      <div class="v3-sidebar-body-scroll">
        <UserBoard v-if="user" :user="user"> </UserBoard>

        <div class="v3-sidebar-nav-section">
          <nav
            data-test-id="sidebar"
            class="d-flex flex-column pb-6 mb-6"
            aria-label="Sidebar Navigation"
          >
            <AppSidebarNav
              v-if="filterSideBarItemsByRole"
              :sidebar-items="filterSideBarItemsByRole"
            />
            <div class="env-version-badge">
              {{ env + " - " + version }}
            </div>
          </nav>
        </div>
      </div>
    </template>
    <template v-else>
      <UserBoard v-if="user" :user="user"> </UserBoard>

      <CustomScrollbar
        class="v3-sidebar-items-container"
        :default-style="true"
        :settings="sidebarNavScrollbarSettings"
      >
        <nav
          data-test-id="sidebar"
          class="d-flex flex-column pb-6 mb-6"
          aria-label="Sidebar Navigation"
        >
          <AppSidebarNav
            v-if="filterSideBarItemsByRole"
            :sidebar-items="filterSideBarItemsByRole"
          />
          <div class="env-version-badge">
            {{ env + " - " + version }}
          </div>
        </nav>
      </CustomScrollbar>
    </template>
  </CSidebar>
</template>

<script>
import { mapGetters } from "vuex"
import moment from "moment-timezone"
import version from "../../../package"
import CustomScrollbar from "@/v3components/CustomScrollbar.vue"
import AppSidebarNav from "./AppSidebarNav.vue"
import SidebarItems from "./SidebarItems.vue"
import SidebarToggler from "./SidebarToggler.vue"
import UserBoard from "./UserBoard.vue"

/** Matches CoreUI mobile sidebar / WCAG reflow (e.g. 1280px viewport at 400% zoom ≈ 320px). */
const SIDEBAR_REFLOW_MQ = "(max-width: 991.98px)"

export default {
  name: "TheSidebar",
  components: { AppSidebarNav, CustomScrollbar, SidebarToggler, UserBoard },
  mixins: [SidebarItems],
  data() {
    return {
      componentKey: 0,
      sidebarToggleObserver: null,
      isSidebarReflowLayout: false,
      sidebarNavScrollbarSettings: {
        suppressScrollX: true,
        suppressScrollY: false
      },
      sidebarReflowMediaQuery: null,
      sidebarReflowMediaQueryHandler: null,
      sidebarKeyboardNavContainer: null
    }
  },
  computed: {
    ...mapGetters({
      userRole: "authentication/getUserRole",
      user: "authentication/user",
      releases: "updates/releases",
      activeModulesNames: "schools/activeModulesNames",
      activeSchool: "schools/activeSchool",
      activeSchoolTimeZone: "schools/activeSchoolTimeZone"
    }),
    env() {
      return import.meta.env.VITE_DD_ENV
    },
    version() {
      return version.version
    },
    isNonProduction() {
      return this.env !== "production"
    },
    activeSchoolId() {
      return this.activeSchool?.id || "N/A"
    },
    activeSchoolCurrentDateTime() {
      // Trigger reactivity with currentTime
      // eslint-disable-next-line no-unused-vars
      const _ = this.currentTime
      return moment()
        .tz(this.activeSchoolTimeZone)
        .format("MM/DD/YYYY hh:mm:ss A")
    },
    show() {
      return !!this.$store.state.layout.sidebarShow
    },
    minimize() {
      return this.$store.state.layout.sidebarMinimize
    },
    newReleases() {
      return this.releases ? this.releases.filter((e) => e.seen == false) : []
    },
    showPopup() {
      return this.releases
        ? this.releases.filter((e) => e.seen == false).length > 0 &&
            !this.alreadypresented
        : false
    },
    filterSideBarItemsByRole() {
      let items = JSON.parse(JSON.stringify(this.sidebarItems))
      //filter by required Role
      items[0]._children = items[0]._children.filter((item) =>
        item.requiredRoles ? item.requiredRoles.includes(this.userRole) : true
      )
      //filter by required Module
      items[0]._children = items[0]._children.filter((item) => {
        if (item.requiredModule) {
          return this.activeModulesNames.includes(item.requiredModule)
        } else {
          return true
        }
      })

      //filter by Route Dependency
      items[0]._children = items[0]._children.filter((item) =>
        item.routeDependency ? this.$store.getters[item.routeDependency] : true
      )

      items[0]._children.map((item) => {
        if (item.items) {
          item.items = item.items.filter((item) =>
            item.requiredRoles
              ? item.requiredRoles.includes(this.userRole)
              : true
          )
          item.items = item.items.filter((item) => {
            if (item.requiredModule) {
              return this.activeModulesNames.includes(item.requiredModule)
            } else {
              return true
            }
          })
          item.items = item.items.filter((item) =>
            item.routeDependency
              ? this.$store.getters[item.routeDependency]
              : true
          )
        }
      })
      items = [...items]
      return items[0]._children
    }
  },
  watch: {
    activeModulesNames: function () {
      // used to force reactivity in filterSideBarItemsByRole and re-render the sidebar after module change
      this.$store.commit("authentication/SET_USER", { ...this.user })
    },
    filterSideBarItemsByRole: function () {
      this.forceComponentReRender()
    }
  },
  mounted() {
    // Start interval for time updates in non-production
    if (this.isNonProduction) {
      this.timeInterval = setInterval(() => {
        this.currentTime = Date.now()
      }, 1000)
    }

    const els = document.querySelectorAll(".nav-link")
    if (els) {
      els.forEach((e) => {
        e.addEventListener(
          "touchend",
          () => {
            window.location.href = e.getAttribute("href")
          },
          false
        )
      })
    }
    this.handleSidebarVisibility()
    this.initSidebarReflowMediaQuery()
    this.initSidebarGroupToggleA11y()
  },
  beforeUnmount() {
    if (this.timeInterval) {
      clearInterval(this.timeInterval)
    }
    this.teardownSidebarReflowMediaQuery()
    this.teardownSidebarGroupToggleA11y()
  },
  methods: {
    initSidebarReflowMediaQuery() {
      if (typeof window === "undefined" || !window.matchMedia) {
        return
      }
      this.sidebarReflowMediaQuery = window.matchMedia(SIDEBAR_REFLOW_MQ)
      this.isSidebarReflowLayout = !!this.sidebarReflowMediaQuery.matches
      this.sidebarReflowMediaQueryHandler = (e) => {
        this.isSidebarReflowLayout = !!e.matches
      }
      this.sidebarReflowMediaQuery.addEventListener(
        "change",
        this.sidebarReflowMediaQueryHandler
      )
    },
    teardownSidebarReflowMediaQuery() {
      if (this.sidebarReflowMediaQuery && this.sidebarReflowMediaQueryHandler) {
        this.sidebarReflowMediaQuery.removeEventListener(
          "change",
          this.sidebarReflowMediaQueryHandler
        )
      }
      this.sidebarReflowMediaQuery = null
      this.sidebarReflowMediaQueryHandler = null
    },
    forceComponentReRender() {
      //TODO: To create a solution for re-rendering the sidebar on sidebar items change (because of the badges and Pass modules avail.)
      this.componentKey += 1
      this.$nextTick(() => {
        this.initSidebarGroupToggleA11y()
      })
    },
    syncSidebarGroupToggleState(toggleElement = null) {
      const toggles = toggleElement
        ? [toggleElement]
        : Array.from(
            document.querySelectorAll(
              ".v3-the-sidebar .nav-link.nav-group-toggle"
            )
          )

      toggles.forEach((toggle) => {
        const parentGroup = toggle.closest(".nav-group")
        const isExpanded =
          parentGroup?.classList?.contains("show") ||
          parentGroup?.classList?.contains("visible")
        toggle.setAttribute("aria-expanded", isExpanded ? "true" : "false")
      })
    },
    onSidebarGroupToggleKeydown(event) {
      if (event.key !== "Enter" && event.key !== " ") {
        return
      }
      event.preventDefault()
      event.currentTarget?.click?.()
      window.requestAnimationFrame(() => {
        this.syncSidebarGroupToggleState(event.currentTarget)
      })
    },
    getVisibleSidebarNavLinks() {
      const navLinks = Array.from(
        document.querySelectorAll(".v3-the-sidebar .sidebar-nav .nav-link")
      )
      return navLinks.filter((element) => element?.offsetParent !== null)
    },
    getPreferredSidebarTabStop() {
      const visibleLinks = this.getVisibleSidebarNavLinks()
      if (!visibleLinks.length) {
        return null
      }

      const activeRouteLink = visibleLinks.find(
        (element) =>
          element.classList.contains("router-link-active") ||
          element.classList.contains("active")
      )
      return activeRouteLink || visibleLinks[0]
    },
    setSidebarSingleTabStopMode() {
      const visibleLinks = this.getVisibleSidebarNavLinks()
      if (!visibleLinks.length) {
        return
      }

      const preferredTabStop = this.getPreferredSidebarTabStop()
      visibleLinks.forEach((element) => {
        if (typeof element.dataset.prevSidebarTabindex === "undefined") {
          element.dataset.prevSidebarTabindex = element.hasAttribute("tabindex")
            ? element.getAttribute("tabindex")
            : "__none__"
        }
        element.setAttribute(
          "tabindex",
          element === preferredTabStop ? "0" : "-1"
        )
      })
    },
    restoreSidebarTabStops() {
      const navLinks = document.querySelectorAll(
        ".v3-the-sidebar .sidebar-nav .nav-link"
      )
      navLinks.forEach((element) => {
        if (typeof element.dataset.prevSidebarTabindex === "undefined") {
          return
        }

        const previousTabindex = element.dataset.prevSidebarTabindex
        if (previousTabindex === "__none__") {
          element.removeAttribute("tabindex")
        } else {
          element.setAttribute("tabindex", previousTabindex)
        }
        delete element.dataset.prevSidebarTabindex
      })

      // Ensure group toggles retain intended keyboard semantics after restore.
      this.enhanceSidebarGroupToggles()
    },
    onSidebarNavFocusIn() {
      this.restoreSidebarTabStops()
    },
    onSidebarNavFocusOut(event) {
      const nextFocusedElement = event.relatedTarget
      if (this.sidebarKeyboardNavContainer?.contains(nextFocusedElement)) {
        return
      }

      window.requestAnimationFrame(() => {
        const activeElement = document.activeElement
        if (!this.sidebarKeyboardNavContainer?.contains(activeElement)) {
          this.setSidebarSingleTabStopMode()
        }
      })
    },
    getSidebarNavFocusableElements() {
      const navLinks = this.getVisibleSidebarNavLinks()
      return navLinks.filter((element) => {
        if (!element) return false
        if (element.getAttribute("aria-disabled") === "true") return false
        return element.getAttribute("tabindex") !== "-1"
      })
    },
    focusSidebarNavElementByOffset(currentElement, offset) {
      const focusableElements = this.getSidebarNavFocusableElements()
      if (!focusableElements.length) return

      const currentIndex = focusableElements.indexOf(currentElement)
      if (currentIndex === -1) {
        focusableElements[0].focus()
        return
      }

      const nextIndex =
        (currentIndex + offset + focusableElements.length) %
        focusableElements.length
      focusableElements[nextIndex]?.focus?.()
    },
    focusMainContentFirstFocusable() {
      const mainRegion = document.querySelector("main.c-main")
      if (!mainRegion) {
        return
      }

      const selectors = [
        "button:not([disabled])",
        "[href]",
        "input:not([disabled])",
        "select:not([disabled])",
        "textarea:not([disabled])",
        "[tabindex]:not([tabindex='-1'])"
      ].join(",")
      const focusableElements = Array.from(
        mainRegion.querySelectorAll(selectors)
      )
      const firstFocusableElement =
        focusableElements.find((element) => {
          if (!element?.offsetParent) {
            return false
          }
          const style = window.getComputedStyle(element)
          return style.display !== "none" && style.visibility !== "hidden"
        }) || null

      if (firstFocusableElement) {
        firstFocusableElement.focus?.()
        return
      }

      // Fallback so keyboard users always land in the active route region.
      const previousTabindex = mainRegion.getAttribute("tabindex")
      mainRegion.setAttribute("tabindex", "-1")
      mainRegion.focus?.()
      if (previousTabindex === null) {
        mainRegion.removeAttribute("tabindex")
      } else {
        mainRegion.setAttribute("tabindex", previousTabindex)
      }
    },
    onSidebarNavKeydown(event) {
      const key = event.key
      const targetElement = event.target?.closest?.(".nav-link")
      if (!targetElement) {
        return
      }

      if (key === "Tab" && !event.shiftKey) {
        event.preventDefault()
        this.focusMainContentFirstFocusable()
        return
      }

      if (key === "ArrowDown") {
        event.preventDefault()
        this.focusSidebarNavElementByOffset(targetElement, 1)
        return
      }

      if (key === "ArrowUp") {
        event.preventDefault()
        this.focusSidebarNavElementByOffset(targetElement, -1)
        return
      }

      if (key === "Home") {
        event.preventDefault()
        this.getSidebarNavFocusableElements()[0]?.focus?.()
        return
      }

      if (key === "End") {
        event.preventDefault()
        const focusableElements = this.getSidebarNavFocusableElements()
        focusableElements[focusableElements.length - 1]?.focus?.()
        return
      }

      if (key === "ArrowRight") {
        const isGroupToggle =
          targetElement.classList.contains("nav-group-toggle")
        if (!isGroupToggle) {
          return
        }
        const parentGroup = targetElement.closest(".nav-group")
        const isExpanded =
          parentGroup?.classList?.contains("show") ||
          parentGroup?.classList?.contains("visible")

        event.preventDefault()
        if (!isExpanded) {
          targetElement.click?.()
          window.requestAnimationFrame(() => {
            this.syncSidebarGroupToggleState(targetElement)
          })
          return
        }

        const firstChildLink = parentGroup?.querySelector(
          ".nav-group-items .nav-link"
        )
        firstChildLink?.focus?.()
        return
      }

      if (key === "ArrowLeft") {
        const parentGroup = targetElement.closest(".nav-group")
        const parentGroupItems = targetElement.closest(".nav-group-items")
        const isGroupToggle =
          targetElement.classList.contains("nav-group-toggle")
        const isExpanded =
          parentGroup?.classList?.contains("show") ||
          parentGroup?.classList?.contains("visible")

        // When focus is inside a submenu, move focus to that submenu's toggle.
        if (parentGroupItems && parentGroup) {
          event.preventDefault()
          const parentToggle =
            parentGroup.querySelector(":scope > .nav-group-toggle") ||
            parentGroup.querySelector(".nav-group-toggle")
          parentToggle?.focus?.()
          return
        }

        // When focused on an expanded group toggle, collapse it.
        if (isGroupToggle && isExpanded) {
          event.preventDefault()
          targetElement.click?.()
          window.requestAnimationFrame(() => {
            this.syncSidebarGroupToggleState(targetElement)
          })
        }
      }
    },
    initSidebarArrowKeyNavigation() {
      const navContainer = document.querySelector(
        ".v3-the-sidebar .sidebar-nav"
      )
      if (this.sidebarKeyboardNavContainer === navContainer) {
        return
      }

      if (this.sidebarKeyboardNavContainer) {
        this.sidebarKeyboardNavContainer.removeEventListener(
          "keydown",
          this.onSidebarNavKeydown
        )
      }

      this.sidebarKeyboardNavContainer = navContainer
      this.sidebarKeyboardNavContainer?.addEventListener(
        "keydown",
        this.onSidebarNavKeydown
      )
      this.sidebarKeyboardNavContainer?.addEventListener(
        "focusin",
        this.onSidebarNavFocusIn
      )
      this.sidebarKeyboardNavContainer?.addEventListener(
        "focusout",
        this.onSidebarNavFocusOut
      )

      const activeElement = document.activeElement
      if (!this.sidebarKeyboardNavContainer?.contains(activeElement)) {
        this.setSidebarSingleTabStopMode()
      } else {
        this.restoreSidebarTabStops()
      }
    },
    enhanceSidebarGroupToggles() {
      const toggles = document.querySelectorAll(
        ".v3-the-sidebar .nav-link.nav-group-toggle"
      )
      if (!toggles?.length) {
        return
      }

      toggles.forEach((toggle) => {
        const toggleText = (toggle.textContent || "").trim()
        const parentGroup = toggle.closest(".nav-group")
        const groupItems = parentGroup?.querySelector(".nav-group-items")
        if (groupItems && !groupItems.id) {
          groupItems.id = `sidebar-submenu-${Math.random().toString(36).slice(2, 10)}`
        }
        toggle.setAttribute("role", "button")
        toggle.setAttribute("tabindex", "0")
        if (toggleText) {
          toggle.setAttribute("aria-label", `${toggleText} submenu`)
        }
        if (groupItems?.id) {
          toggle.setAttribute("aria-controls", groupItems.id)
        }
        if (!toggle.hasAttribute("aria-haspopup")) {
          toggle.setAttribute("aria-haspopup", "true")
        }
        if (!toggle.dataset.keyboardA11yBound) {
          toggle.addEventListener("keydown", this.onSidebarGroupToggleKeydown)
          toggle.dataset.keyboardA11yBound = "true"
        }
      })

      this.syncSidebarGroupToggleState()
    },
    initSidebarGroupToggleA11y() {
      this.$nextTick(() => {
        this.enhanceSidebarGroupToggles()
        this.initSidebarArrowKeyNavigation()
        if (this.sidebarToggleObserver) {
          return
        }
        const navRoot = document.querySelector(".v3-the-sidebar")
        if (!navRoot) {
          return
        }
        this.sidebarToggleObserver = new MutationObserver(() => {
          this.enhanceSidebarGroupToggles()
          this.initSidebarArrowKeyNavigation()
        })
        this.sidebarToggleObserver.observe(navRoot, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ["class"]
        })
      })
    },
    teardownSidebarGroupToggleA11y() {
      const toggles = document.querySelectorAll(
        ".v3-the-sidebar .nav-link.nav-group-toggle"
      )
      toggles.forEach((toggle) => {
        if (toggle.dataset.keyboardA11yBound) {
          toggle.removeEventListener(
            "keydown",
            this.onSidebarGroupToggleKeydown
          )
          delete toggle.dataset.keyboardA11yBound
        }
      })
      if (this.sidebarToggleObserver) {
        this.sidebarToggleObserver.disconnect()
        this.sidebarToggleObserver = null
      }
      if (this.sidebarKeyboardNavContainer) {
        this.sidebarKeyboardNavContainer.removeEventListener(
          "keydown",
          this.onSidebarNavKeydown
        )
        this.sidebarKeyboardNavContainer.removeEventListener(
          "focusin",
          this.onSidebarNavFocusIn
        )
        this.sidebarKeyboardNavContainer.removeEventListener(
          "focusout",
          this.onSidebarNavFocusOut
        )
        this.sidebarKeyboardNavContainer = null
      }
    },
    handleSidebarVisibility() {
      setTimeout(() => {
        this.$store.commit("layout/set", [
          "sidebarShow",
          window.innerWidth > 992
        ])
      }, 0)
      window.addEventListener("resize", (e) => {
        const windowWidth = e.target.innerWidth
        if (windowWidth) {
          this.$store.commit("layout/set", ["sidebarShow", windowWidth > 992])
        }
      })
    }
  }
}
</script>
